import { create } from 'zustand';
import type { ProfileImageCropTarget } from '@/features/profile/edit/crop/profileImageCropConfig';
import { useProfileEditStore } from '@/features/profile/edit/profileEditStore';
import { uploadProfileAvatar, uploadProfileBackground } from '@/features/profile/api';
import { getCurrentUserId } from '@/features/auth/session';

interface ProfileImageCropState {
  isOpen: boolean;
  target: ProfileImageCropTarget | null;
  imageSrc: string | null;
  openForCrop: (target: ProfileImageCropTarget, file: File) => void;
  cancel: () => void;
  applyCroppedBlob: (blob: Blob) => void;
}

/**
 * Push the cropped blob to `/mediaservice/api/v3/media` so the cover/avatar
 * is persisted on the backend. The local blob preview already drives the
 * UI, so this runs as a fire-and-forget side effect; if it succeeds we
 * swap the blob URL for the canonical CDN URL returned by the server.
 */
function uploadCroppedMedia(target: ProfileImageCropTarget, blob: Blob): void {
  const userId = getCurrentUserId();
  if (!userId) return;

  const upload = target === 'avatar' ? uploadProfileAvatar : uploadProfileBackground;

  void upload(userId, blob)
    .then((result) => {
      if (!result.url) return;
      useProfileEditStore.setState((state) => {
        if (target === 'avatar') {
          if (state.draft.avatarUrl?.startsWith('blob:')) {
            URL.revokeObjectURL(state.draft.avatarUrl);
          }
          return { draft: { ...state.draft, avatarUrl: result.url } };
        }
        if (state.draft.bannerUrl?.startsWith('blob:')) {
          URL.revokeObjectURL(state.draft.bannerUrl);
        }
        return { draft: { ...state.draft, bannerUrl: result.url } };
      });
    })
    .catch((error) => {
      console.error(`[profile] failed to upload ${target} image`, error);
    });
}

function revokeSrc(src: string | null): void {
  if (src?.startsWith('blob:')) {
    URL.revokeObjectURL(src);
  }
}

function replacePreview(previous: string | undefined, next: string): string {
  if (previous?.startsWith('blob:')) {
    URL.revokeObjectURL(previous);
  }
  return next;
}

export const useProfileImageCropStore = create<ProfileImageCropState>((set, get) => ({
  isOpen: false,
  target: null,
  imageSrc: null,

  openForCrop: (target, file) => {
    const previous = get().imageSrc;
    revokeSrc(previous);
    const imageSrc = URL.createObjectURL(file);
    set({ isOpen: true, target, imageSrc });
  },

  cancel: () => {
    const { imageSrc } = get();
    revokeSrc(imageSrc);
    set({ isOpen: false, target: null, imageSrc: null });
  },

  applyCroppedBlob: (blob) => {
    const { target, imageSrc } = get();
    if (!target) {
      return;
    }

    const previewUrl = URL.createObjectURL(blob);

    useProfileEditStore.setState((state) => {
      if (target === 'avatar') {
        return {
          draft: {
            ...state.draft,
            avatarUrl: replacePreview(state.draft.avatarUrl, previewUrl),
          },
          isDirty: true,
        };
      }
      return {
        draft: {
          ...state.draft,
          bannerUrl: replacePreview(state.draft.bannerUrl, previewUrl),
        },
        isDirty: true,
      };
    });

    revokeSrc(imageSrc);
    set({ isOpen: false, target: null, imageSrc: null });

    // Persist the cropped image on the backend (mediaservice). Runs in the
    // background — the local blob preview keeps the UI responsive while the
    // upload is in-flight, and we swap to the CDN URL once it returns.
    uploadCroppedMedia(target, blob);
  },
}));

/** Opens the crop modal for a profile or banner image file. */
export function openProfileImageCrop(target: ProfileImageCropTarget, file: File): void {
  useProfileImageCropStore.getState().openForCrop(target, file);
}
